import { PublicHeader } from "../../components/Header/PublicHeader";
import { useEffect, useState } from 'react';
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { getPublicTeamRoster } from '../../api/teamRoster';
import { useAuth } from '../../context/AuthContext';
import styles from './TeamRosterPage.module.css';

const PAGE_SIZE = 24;
const TRACK_DISPLAY_NAMES = {
  'Партнеры': 'ЮФУ.Про Партнеров',
  'Стартапы': 'ЮФУ.Про Стартапы',
  'Исследования': 'ЮФУ.Про Исследования',
  'Общество': 'ЮФУ.Про Общество',
  'Организация': 'ЮФУ.Про Организацию',
};

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timerId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timerId);
  }, [value, delayMs]);

  return debounced;
}

function splitMentors(value) {
  return String(value || '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

function getTrackDisplayName(value) {
  return TRACK_DISPLAY_NAMES[String(value || '').trim()] || String(value || '').trim();
}

export function TeamRosterPage() {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debouncedQuery = useDebouncedValue(query);

  useEffect(() => {
    setOffset(0);
  }, [debouncedQuery]);

  useEffect(() => {
    let isCancelled = false;
    setLoading(true);
    setError('');

    getPublicTeamRoster({ query: debouncedQuery, limit: PAGE_SIZE, offset })
      .then((response) => {
        if (isCancelled) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
        setTotal(Number.isFinite(response?.total) ? response.total : 0);
      })
      .catch((err) => {
        if (isCancelled) return;
        setItems([]);
        setTotal(0);
        setError(err.message || 'Не удалось загрузить составы команд.');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery, offset]);

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <PublicHeader />
      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>ЮФУ.Про / Команды</p>
          <h1 className={styles.title}>Предварительный список команд</h1>
          <p className={styles.description}>
            Быстрый список команд с поиском по коду, теме, наставникам и участникам.
            Откройте строку, чтобы посмотреть полный состав и перейти к своей команде на платформе.
          </p>
        </div>
        <div className={styles.summaryCard}>
          <span>Команд в списке</span>
          <strong>{total}</strong>
          <p>Данные обновляются из загруженной таблицы координаторов.</p>
        </div>
      </section>

      <section className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Поиск по коду, теме, наставнику или участнику"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <div className={styles.empty}>Загружаем составы команд...</div> : null}

      {!loading && !items.length ? (
        <div className={styles.empty}>
          {debouncedQuery ? 'По этому запросу команды не найдены.' : 'Предварительный список команд пока не загружен.'}
        </div>
      ) : null}

      {!loading && items.length ? (
        <>
          <section className={styles.list}>
            {items.map((item) => {
              const mentors = splitMentors(item.mentor_name);
              return (
                <details key={item.entry_id} className={styles.row}>
                  <summary className={styles.rowSummary}>
                    <span className={styles.rowArrow} aria-hidden="true" />
                    <div className={styles.codeCell}>
                      <span className={styles.codeBadge}>{item.project_code}</span>
                      {item.track_label ? <span className={styles.trackBadge}>{getTrackDisplayName(item.track_label)}</span> : null}
                    </div>
                    <div className={styles.topicCell}>
                      <strong>{item.topic || 'Тема уточняется'}</strong>
                    </div>
                    <div className={styles.mentorCell}>
                      {mentors.length ? mentors.join(', ') : '—'}
                    </div>
                    <div className={styles.countCell}>{item.member_count}</div>
                  </summary>

                  <div className={styles.rowDetails}>
                    <div className={styles.detailsMeta}>
                      <div>
                        <span>Код проекта</span>
                        <strong>{item.project_code}</strong>
                      </div>
                      <div>
                        <span>Трек</span>
                        <strong>{item.track_label ? getTrackDisplayName(item.track_label) : '—'}</strong>
                      </div>
                      <div>
                        <span>Наставники</span>
                        <strong>{mentors.length ? mentors.join(', ') : '—'}</strong>
                      </div>
                      <div>
                        <span>Команда на платформе</span>
                        <Link to={isAuthenticated ? '/team' : '/login'} className={styles.teamLink}>
                          Перейти к команде
                        </Link>
                      </div>
                    </div>

                    {item.case_info ? (
                      <div className={styles.caseBlock}>
                        <div className={styles.caseHeader}>
                          <span>Проектное задание</span>
                          {item.case_info.track_label ? (
                            <strong>{getTrackDisplayName(item.case_info.track_label)}</strong>
                          ) : null}
                        </div>
                        <h3 className={styles.caseTitle}>{item.case_info.title}</h3>
                        <p className={styles.casePartner}>{item.case_info.partner_name}</p>
                        <p className={styles.caseProblem}>{item.case_info.problem}</p>
                        {item.case_info.expected_solution ? (
                          <p className={styles.caseExpected}>{item.case_info.expected_solution}</p>
                        ) : null}
                      </div>
                    ) : null}

                    <div className={styles.vacanciesBlock}>
                      <div className={styles.vacanciesHeader}>
                        <span>Актуальные вакансии</span>
                        <strong>{item.vacancies?.length || 0}</strong>
                      </div>
                      {item.vacancies && item.vacancies.length ? (
                        <ul className={styles.vacanciesList}>
                          {item.vacancies.map((vacancy) => (
                            <li key={vacancy.vacancy_id} className={styles.vacancyItem}>
                              <strong>{vacancy.title}</strong>
                              {vacancy.description ? <p>{vacancy.description}</p> : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={styles.vacanciesEmpty}>Команда пока не публиковала вакансии.</p>
                      )}
                    </div>

                    <div className={styles.membersBlock}>
                      <p className={styles.membersTitle}>Состав команды</p>
                      <ol className={styles.membersList}>
                        {item.members.map((member) => (
                          <li key={`${item.entry_id}-${member}`}>{member}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </details>
              );
            })}
          </section>

          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              disabled={offset === 0}
            >
              Назад
            </button>
            <span>Страница {currentPage} из {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
            >
              Далее
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
